import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Users, Clock, GraduationCap, Brain, Theater } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const HERO =
  "https://images.unsplash.com/photo-1471877325906-aee7c2240b5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHx0aGVhdHJlJTIwc3RhZ2UlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzgzMjIzNTk5fDA&ixlib=rb-4.1.0&q=85";

const facts = [
  { icon: Theater, label: "Format", value: "One act play" },
  { icon: Users, label: "Cast", value: "Two actors" },
  { icon: Brain, label: "Theme", value: "Psychosis & mental health" },
  { icon: GraduationCap, label: "Audience", value: "Age 14+" },
];

export default function MindsChaos() {
  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="relative h-[70vh] min-h-[460px] flex items-end overflow-hidden">
        <img src={HERO} alt="Spotlight on a dark stage" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/60 to-[#1A1A1A]/30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-16 w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="label-caps text-[#D4AF37] mb-4"
          >
            An original play by Jack Pritchard
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light text-[#F7F5F0] leading-none"
          >
            Mind's Chaos
            <span className="block font-serif-display italic text-2xl sm:text-3xl text-[#F7F5F0]/80 mt-3">
              (a theatre in education show)
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}
            className="text-lg text-[#F7F5F0]/85 max-w-2xl mt-5 leading-relaxed"
          >
            A one act, two actor play about psychosis. Educational, thought provoking and fun,
            written for ages 14+.
          </motion.p>
        </div>
      </section>

      {/* FACTS */}
      <section className="bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {facts.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.label} className="flex items-start gap-3">
                <Icon size={22} className="text-[#D4AF37] mt-1 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="label-caps text-[0.6rem] text-[#F7F5F0]/50">{f.label}</p>
                  <p className="text-[#F7F5F0] text-sm mt-1">{f.value}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ABOUT THE PLAY */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7">
          <p className="label-caps text-[#7A1C28] mb-4">About the play</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight mb-6">
            A window into a misunderstood experience.
          </h2>
          <p className="text-[#4A4A4A] leading-relaxed mb-4">
            Mind's Chaos is a fast paced, two hander that takes young audiences inside the
            experience of psychosis with honesty, warmth and a surprising amount of humour.
            It cuts through stigma and myth, replacing fear with understanding.
          </p>
          <p className="text-[#4A4A4A] leading-relaxed mb-4">
            Suitable for ages 14 and over, the play runs as a single act with a simple,
            flexible staging that fits a hall, studio or classroom. As well as secondary
            schools, it can be taken into sixth forms, universities or fringe theatre venues,
            and works beautifully alongside the follow up workshop and Q&amp;A.
          </p>
          <p className="text-[#4A4A4A] leading-relaxed">
            The result is theatre that entertains first and teaches naturally, leaving pupils
            thinking, talking and more compassionate about mental health.
          </p>
        </div>

        <div className="md:col-span-5">
          <div className="bg-[#EFEBE1] border border-[#E5E2D9] p-8">
            <p className="label-caps text-[#7A1C28] mb-5">Curriculum links</p>
            <ul className="space-y-5">
              <li>
                <p className="font-serif-display text-2xl mb-1">PSHE</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">
                  Mental health and wellbeing, empathy, reducing stigma, and knowing where to
                  find support.
                </p>
              </li>
              <li>
                <p className="font-serif-display text-2xl mb-1">Drama</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed">
                  Live performance analysis, theatrical devices, characterisation and
                  practitioner style, supporting written and practical study.
                </p>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT IT OFFERS */}
      <section className="bg-[#EFEBE1]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <p className="label-caps text-[#7A1C28] mb-10">What schools can expect</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Brain, title: "Educational", text: "A clear, accurate and sensitive portrayal of psychosis that supports mental health learning." },
              { icon: Theater, title: "Thought provoking", text: "Themes that spark genuine reflection and discussion long after the performance ends." },
              { icon: Users, title: "Fun & engaging", text: "Warmth and humour keep pupils gripped, so the message lands without ever feeling like a lecture." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-[#FFFFFF] border border-[#E5E2D9] p-8"
                >
                  <Icon size={28} className="text-[#7A1C28] mb-6" strokeWidth={1.4} />
                  <h3 className="font-serif-display text-2xl mb-2">{c.title}</h3>
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">{c.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#7A1C28] text-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight">
              Bring Mind's Chaos to your school.
            </h2>
            <p className="text-[#F7F5F0]/80 mt-4 max-w-xl leading-relaxed">
              Get in touch to discuss availability, pricing and how the performance and workshop
              can fit your timetable.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="minds-chaos-book-button"
            className="shrink-0 bg-[#F7F5F0] text-[#1A1A1A] hover:bg-[#D4AF37] transition-colors px-8 py-4 label-caps text-[0.7rem] flex items-center gap-2"
          >
            Enquire now <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
